import {
  type CompleteMultipartUploadCommandOutput,
  DeleteObjectCommand,
  GetObjectCommand,
  HeadObjectCommand,
  type HeadObjectCommandOutput,
  ListObjectsV2Command,
  NoSuchKey,
  PutObjectCommand,
  S3Client,
  type S3ClientConfigType,
} from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { getLogger } from '@tig-dpqa-cloud/logger';
import { type Readable } from 'node:stream';

const getS3Config = (): Partial<S3ClientConfigType> => {
  const logger = getLogger({ name: 's3-client-initialization' });
  const config = {};
  if (process.env['DPQA_USE_S3_TA_ENDPOINT'] === 'true') {
    logger.info('Using S3 Transfer Acceleration endpoint');
    Object.assign(config, {
      useAccelerateEndpoint: true,
    });
  }

  if (process.env['INTERNAL_DPQA_S3_ENDPOINT']) {
    logger.info({ endpoint: process.env['INTERNAL_DPQA_S3_ENDPOINT'] }, 'Using internal S3 endpoint for DPQA');
    Object.assign(config, {
      endpoint: process.env['INTERNAL_DPQA_S3_ENDPOINT'],
      forcePathStyle: true,
    });
  }

  return config;
};

const extension = 'tar.gz';
let s3Client: S3Client;
try {
  const useAccelerateEndpoint = process.env['DPQA_USE_S3_TA_ENDPOINT'] === 'true';
  s3Client = new S3Client(getS3Config());
  const logger = getLogger({ name: 's3-client-initialization' });
  logger.info({ useAccelerateEndpoint }, 'Initialized S3 client with accelerate endpoint');
} catch (_error) {
  s3Client = new S3Client();
}

const uuidv4Regex = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const keyRegex = new RegExp(
  `(?<tenantId>${uuidv4Regex})/(?<slideScanId>${uuidv4Regex}).${extension.replaceAll('.', '\\.')}`,
);

export const getIdsFromObjectKey = (key: string): { tenantId: string; slideScanId: string } => {
  const result = keyRegex.exec(key);

  if (!result || !result.groups?.['tenantId'] || !result.groups?.['slideScanId']) {
    throw new Error('S3 object key could not be parsed.');
  }
  return { tenantId: result.groups['tenantId'], slideScanId: result.groups['slideScanId'] };
};

export const getObjectKeyForSlideScanFile = (tenantId: string, slideSanId: string, filename: string) =>
  `${tenantId}/${slideSanId}/${filename}`;

export const getObjectKeyForSlideScanArchive = (tenantId: string) => (slideSanId: string) =>
  `${tenantId}/${slideSanId}.${extension}`;

export type GetObjectKeyForSlideScanArchiveFn = ReturnType<typeof getObjectKeyForSlideScanArchive>;

export const convertReadableToString = async (stream: Readable, encoding: BufferEncoding = 'utf8'): Promise<string> => {
  const chunks: Buffer[] = [];
  for await (const ch of stream) {
    chunks.push(typeof ch === 'string' || ch instanceof String ? Buffer.from(ch, encoding) : ch);
  }
  return Buffer.concat(chunks).toString(encoding);
};

export const convertReadableToBuffer = async (stream: Readable): Promise<Buffer> => {
  const chunks: Buffer[] = [];
  for await (const chunk of stream) {
    chunks.push(chunk as Buffer);
  }
  return Buffer.concat(chunks);
};

export const getS3ObjectAsStream = async (Bucket: string, Key: string): Promise<[Readable, number | undefined]> => {
  const logger = getLogger({ name: 's3-utils-object-as-stream' });

  logger.debug({ Bucket, Key }, 'Retrieve file from S3');
  const { Body, ContentLength } = await s3Client.send(new GetObjectCommand({ Bucket, Key }));
  if (!Body) {
    throw new Error('Could not read file from S3');
  }
  return [Body as Readable, ContentLength];
};

export const tryGetS3ObjectAsStream =
  async (Bucket: string, Key: string): Promise<[Readable | undefined, any | undefined]> => {
    const logger = getLogger({ name: 's3-utils-try-object-as-stream' });
    try {
      const [readable] = await getS3ObjectAsStream(Bucket, Key);
      return [readable, undefined];
    } catch (error: any) {
      if (error instanceof NoSuchKey) {
        logger.debug({ Bucket, Key }, 'File not found in S3');
        return [undefined, undefined];
      }
      return [undefined, error];
    }
  };

export type GetS3ObjectAStreamFn = typeof getS3ObjectAsStream;

export type TryGetS3ObjectAStream = typeof tryGetS3ObjectAsStream;

export const putS3ObjectAsStream = (
  Bucket: string,
  Key: string,
  Body: Readable,
): Promise<CompleteMultipartUploadCommandOutput> => {
  const logger = getLogger({ name: 's3-utils-put-object-as-stream' });

  logger.debug({ Bucket, Key }, 'Uploading file to S3');
  const upload = new Upload({
    client: s3Client,
    partSize: 52428800, // 50MB per part,
    queueSize: 5, // 5 parallel uploads.
    params: { Bucket, Key, Body },
  }).on('httpUploadProgress', (progress) => {
    logger.debug({ ...progress, Bucket, Key, memory: process.memoryUsage.rss() }, 'Multipart S3 upload progress');
  });
  return upload.done();
};

export type PutS3ObjectAStreamFn = typeof putS3ObjectAsStream;

export const deleteS3Object = async (Bucket: string, Key: string): Promise<void> => {
  const logger = getLogger({ name: 's3-utils-delete-object' });

  logger.debug({ Bucket, Key }, 'Deleting file from S3');
  await s3Client.send(
    new DeleteObjectCommand({ Bucket, Key }),
  );
};

export type DeleteS3ObjectFn = typeof deleteS3Object;

export const listObjects = async (Bucket: string, Prefix?: string, Delimiter?: string): Promise<string[]> => {
  const logger = getLogger({ name: 's3-utils-list-objects' });

  logger.debug({ Bucket, Prefix }, 'Listing objects in S3 bucket');
  // TODO if needed, support pagination (use continuationToken in ls response and recurse with it)
  const ls = await s3Client.send(new ListObjectsV2Command({ Bucket, Prefix, Delimiter }));
  if (Delimiter) {
    return ls.CommonPrefixes?.map((p) => p.Prefix).filter((s): s is string => !!s) || [];
  }
  return ls.Contents?.map((c) => c.Key).filter((s): s is string => !!s) || [];
};

export type ListObjectsFn = typeof listObjects;

export const getPreSignUploadPackageUrlForFn =
  (Bucket: string, tenantId: string) =>
    (slideScanId: string): Promise<string> =>
      getSignedUrl(
        s3Client,
        new PutObjectCommand({ Bucket, Key: getObjectKeyForSlideScanArchive(tenantId)(slideScanId) }),
        { expiresIn: 3600 },
      );

export type PreSignUploadPackageUrlForFn = ReturnType<typeof getPreSignUploadPackageUrlForFn>;

export const getSignedDownloadUriFn =
  (Bucket: string) =>
    (Key: string, expiresIn: number = 3600): Promise<string> =>
      getSignedUrl(s3Client, new GetObjectCommand({ Bucket, Key }), { expiresIn });

export type getSignedDownloadUriFunction = ReturnType<typeof getSignedDownloadUriFn>;

export const getHeadObjectFn =
  (Bucket: string) =>
    (Key: string): Promise<HeadObjectCommandOutput> =>
      s3Client.send(new HeadObjectCommand({ Bucket, Key }));

export type GetHeadObjectFunction = ReturnType<typeof getHeadObjectFn>;

export const metadataConfigPath = 'config/input/metadata';

export const getMetadataConfigRef = (versionId?: string) => versionId ? `${metadataConfigPath}#${versionId}` : metadataConfigPath;

export const getMetadataConfigRefForTenant = (tenantId: string, versionId?: string) => `${tenantId}/${getMetadataConfigRef(versionId)}`;
